import RailsApiResponse from '@apptypes/api/RailsApiResponse.ts';
import { ComponentName, ComponentSettings, usaOnlyBrand } from '@constants';
import useGTM from '@hooks/useGTM.tsx';
import { useI18n } from '@hooks/useI18n.tsx';
import { useModal } from '@hooks/useModal.tsx';
import loadable from '@loadable/component';
import { gtmDatakey } from '@utils/GoogleTagManager.ts';
import { postApi } from '@utils/apiUtils.ts';
import { removeFalsyFromObject } from '@utils/index.ts';
import { useEffect } from 'react';

const LoadableValidateGeoModal = loadable(
  () => import('./components/modals/ValidateGeoModal'),
);
const LoadableTermsAndConditionsModal = loadable(
  () => import('./components/modals/TermsAndConditionsModal'),
);
const LoadableResponsibleGamblingModal = loadable(
  () => import('./components/modals/ResponsibleGamblingModal'),
);
const LoadableGeoComplyModal = loadable(
  () => import('./components/modals/GeoComplyModal'),
);
const LoadableDepositThresholdModal = loadable(
  () => import('./components/modals/DepositThresholdModal'),
);
const LoadableCookiePolicyModal = loadable(
  () => import('./components/modals/CookiePolicyModal'),
);
const LoadableAddBankAccountModal = loadable(
  () => import('./components/modals/AddBankAccountModal'),
);
const LoadablePromoClaimModal = loadable(
  () => import('./components/modals/PromoClaimModal'),
);

const LoadableCasinoGameInfoModal = loadable(
  () => import('./components/modals/CasinoGameInfoModal'),
);
const LoadableDynamicKBAQuestionsModal = loadable(
  () => import('./components/modals/questionsKBAModal'),
);
const LoadableLimitsReaffirmationModal = loadable(
  () => import('./components/modals/LimitsReaffirmationModal'),
);
const LoadableSessionReminderModal = loadable(
  () => import('./components/modals/SessionReminderModal'),
);
const LoadableOneTimePasscodeModal = loadable(
  () => import('./components/modals/OneTimePasscodeModal'),
);
const LoadableValidationFailedModal = loadable(
  () => import('./components/modals/ValidationFailedModal'),
);
const LoadableTimeoutNotificationModal = loadable(
  () => import('./components/modals/TimeoutNotificationModal'),
);
const LoadableBonusActivatedOnRegModal = loadable(
  () => import('./components/modals/BonusActivatedOnRegModal'),
);
const LoadablePlayerDisabledModal = loadable(
  () => import('./components/modals/PlayerDisabledModal'),
);
const LoadableQuickDepositModal = loadable(
  () => import('./components/modals/QuickDepositModal'),
);
const LoadableLogoutMessageModal = loadable(
  () => import('./components/modals/LogoutMessageModal'),
);
const LoadableW9WinningsModal = loadable(
  () => import('./components/modals/W9WinnningsModal'),
);
const LoadablePlayerGambleTimeReachedModal = loadable(
  () => import('./components/modals/PlayerGambleTimeReachedModal'),
);
const LoadableAlreadyPlayingGameModal = loadable(
  () => import('./components/modals/AlreadyPlayingGameModal'),
);

const addBankAccountSubmit = async data => {
  removeFalsyFromObject(data);
  const res = await postApi<RailsApiResponse<null>>(
    '/restapi/v1/user/bank',
    data,
  ).catch((err: RailsApiResponse<null>) => err);
  return res.Success || res.Message || false;
};

const Modals = () => {
  const { hasTranslations } = useI18n();
  const isLogoutMessageModalEnabled =
    window.__config__.componentSettings?.logout?.logoutMessageModalEnabled ??
    false;
  const sendDataToGTM = useGTM();
  const { activeModal } = useModal();

  useEffect(() => {
    if (hasTranslations && activeModal && typeof activeModal === 'string') {
      sendDataToGTM({
        [gtmDatakey('ActiveModal')]: activeModal,
        event: 'ModalActiveChange',
      });
    }
  }, [hasTranslations, activeModal, sendDataToGTM]);

  if (!hasTranslations) {
    return null;
  }
  // TODO: Implement a preloader component for modals that manage their own opening and closing behavior.
  return (
    <>
      <LoadableTermsAndConditionsModal />
      {ComponentSettings?.modals.ResponsibleGambling && (
        <LoadableResponsibleGamblingModal />
      )}
      {ComponentSettings?.modals.DepositThreshold && (
        <LoadableDepositThresholdModal />
      )}
      <LoadableGeoComplyModal />
      <LoadableTimeoutNotificationModal />
      <LoadableBonusActivatedOnRegModal />
      <LoadableValidationFailedModal />
      {usaOnlyBrand && <LoadableW9WinningsModal />}
      <LoadablePlayerDisabledModal />
      {isLogoutMessageModalEnabled && <LoadableLogoutMessageModal />}
      {activeModal === ComponentName.CookiesModal && (
        <LoadableCookiePolicyModal />
      )}
      <LoadableQuickDepositModal />
      {activeModal === ComponentName.AddBankAccountModal && (
        <LoadableAddBankAccountModal onSubmit={addBankAccountSubmit} />
      )}
      {activeModal === ComponentName.CasinoGameInfoModal && (
        <LoadableCasinoGameInfoModal />
      )}
      {ComponentSettings?.modals.KBAQuestions && (
        <LoadableDynamicKBAQuestionsModal />
      )}
      <LoadableLimitsReaffirmationModal />
      <LoadableSessionReminderModal />
      <LoadableOneTimePasscodeModal />
      <LoadablePlayerGambleTimeReachedModal />
      <LoadableAlreadyPlayingGameModal />
    </>
  );
};

export default Modals;
